import React, { useState, useEffect, useCallback } from 'react';
import { B } from '../../constants';
import { borderRadius, spacing, transitions } from '../../designTokens';
import Button from './Button';

const variantColorMap = {
  default: { bg: 'transparent', icon: B.night, title: B.night, desc: B.muted, border: 'transparent' },
  error: { bg: '#fef2f2', icon: B.error, title: '#991b1b', desc: '#b91c1c', border: '#fecaca' },
  success: { bg: '#f0fdf4', icon: B.success, title: '#166534', desc: '#15803d', border: '#bbf7d0' },
  warning: { bg: '#fffbeb', icon: B.warning, title: '#92400e', desc: '#a16207', border: '#fde68a' },
  info: { bg: '#eff6ff', icon: B.info, title: '#1e40af', desc: '#1d4ed8', border: '#bfdbfe' },
  minimal: { bg: 'transparent', icon: B.night, title: B.night, desc: B.muted, border: 'transparent' },
};

const sizeMap = {
  sm: { containerPadding: spacing.lg, iconSize: 40, titleSize: 18, minHeight: 200, descSize: 13, gap: spacing.sm },
  md: { containerPadding: spacing.xxl, iconSize: 56, titleSize: 22, minHeight: 300, descSize: 14, gap: spacing.md },
  lg: { containerPadding: spacing.xxxl, iconSize: 72, titleSize: 26, minHeight: 400, descSize: 15, gap: spacing.lg },
};

const animationMap = {
  fadeUp: 'fadeUp',
  fadeIn: 'fadeIn',
  scaleIn: 'scaleIn',
  none: undefined,
};

const defaultIcons = {
  error: '❌',
  success: '✅',
  warning: '⚠️',
  info: 'ℹ️',
};

function EmptyState({
  as: Tag = 'div',
  icon,
  title,
  description,
  action,
  actionVariant = 'primary',
  actionProps,
  variant = 'default',
  illustration,
  illustrationSize,
  illustrationStyle,
  size = 'md',
  radius,
  fullWidth,
  iconSize,
  animation = 'fadeUp',
  animationDuration = 'normal',
  containerStyle,
  secondaryAction,
  secondaryActionVariant = 'ghost',
  illustrationFallback,
  align = 'center',
  maxWidth,
  titleStyle,
  descriptionStyle,
  role,
  'aria-live': ariaLive,
  showFocusRing = true,
  focusRingColor = B.forest,
}) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReducedMotion(mq.matches);
      const handler = (e) => setReducedMotion(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch { return undefined; }
  }, []);

  const s = sizeMap[size] || sizeMap.md;
  const vc = variantColorMap[variant] || variantColorMap.default;
  const isSemantic = variant !== 'default' && variant !== 'minimal';
  const actualRole = role || (isSemantic ? 'alert' : 'status');
  const actualAriaLive = ariaLive || (isSemantic ? 'assertive' : 'polite');
  const actualRadius = radius ? (borderRadius[radius] || radius) : undefined;

  const animKey = animation === 'none' ? undefined : (animationMap[animation] || animationMap.fadeUp);
  const animDur = reducedMotion ? undefined : (transitions[animationDuration] || animationDuration || transitions.normal);

  const iconElSize = iconSize || s.iconSize;

  const effectiveIcon = icon || (isSemantic ? defaultIcons[variant] : '📭');

  const showIllustration = illustration && !imgError;

  const handleImgError = useCallback(() => {
    setImgError(true);
  }, []);

  const handleSecondaryClick = useCallback((e) => {
    secondaryAction?.onClick?.(e);
  }, [secondaryAction]);

  return React.createElement(Tag, {
    role: actualRole,
    'aria-live': actualAriaLive,
    style: {
      display: 'flex', flexDirection: 'column',
      alignItems: align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end',
      justifyContent: 'center',
      textAlign: align,
      padding: s.containerPadding,
      minHeight: variant === 'minimal' ? 'auto' : s.minHeight,
      background: vc.bg,
      border: variant !== 'default' && variant !== 'minimal' ? `1px solid ${vc.border}` : undefined,
      borderRadius: fullWidth && actualRadius ? actualRadius : undefined,
      width: fullWidth ? '100%' : undefined,
      boxSizing: 'border-box',
      ...(!reducedMotion && animKey ? {
        animation: `${animKey} ${animDur}`,
      } : {}),
      ...containerStyle,
    },
  },
    variant !== 'minimal' && (
      <div
        style={{
          fontSize: typeof iconElSize === 'number' ? iconElSize : iconElSize,
          marginBottom: s.gap,
          lineHeight: 1,
          color: isSemantic ? vc.icon : undefined,
          ...illustrationStyle,
        }}
        aria-hidden="true"
      >
        {showIllustration ? (
          typeof illustration === 'string' ? (
            <img
              src={illustration}
              alt=""
              style={{
                width: typeof iconElSize === 'number' ? iconElSize : iconElSize,
                height: typeof iconElSize === 'number' ? iconElSize : iconElSize,
                objectFit: 'contain',
                ...illustrationStyle,
              }}
              onError={handleImgError}
            />
          ) : illustration
        ) : imgError && illustrationFallback ? (
          illustrationFallback
        ) : (
          effectiveIcon
        )}
      </div>
    ),
    React.createElement(Tag === 'div' ? 'h3' : 'h3', {
      style: {
        fontFamily: "'Playfair Display',serif",
        fontSize: s.titleSize, fontWeight: 700,
        color: vc.title, marginBottom: spacing.xs,
        margin: 0,
        ...titleStyle,
      },
    }, title),
    description && React.createElement(Tag === 'div' ? 'p' : 'p', {
      style: {
        fontFamily: "'Montserrat',sans-serif",
        fontSize: s.descSize, color: vc.desc,
        maxWidth: maxWidth || 360, lineHeight: 1.6,
        margin: 0,
        marginTop: spacing.xs,
        marginBottom: action || secondaryAction ? s.gap : 0,
        ...descriptionStyle,
      },
    }, description),
    (action || secondaryAction) && React.createElement('div', {
      style: {
        display: 'flex',
        gap: spacing.sm,
        flexWrap: 'wrap',
        justifyContent: align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end',
        marginTop: spacing.sm,
      },
    },
      action && React.createElement(Button, {
        variant: actionVariant,
        onClick: action.onClick,
        showFocusRing,
        focusRingColor,
        size: size === 'lg' ? 'md' : size === 'sm' ? 'xs' : 'sm',
        ...actionProps,
      }, action.label),
      secondaryAction && React.createElement(Button, {
        variant: secondaryActionVariant,
        onClick: handleSecondaryClick,
        showFocusRing,
        focusRingColor,
        size: size === 'lg' ? 'md' : size === 'sm' ? 'xs' : 'sm',
      }, secondaryAction.label),
    ),
  );
}

export default EmptyState;
